/**
 * The analytics consent state machine, as pure functions (unit tested in
 * `consent-policy.test.ts`). The browser runtime (`posthog-client.ts`) only
 * executes what these functions decide.
 *
 * Two modes:
 *
 * - `cookieless` (default: no decision yet, "Necessary only", GPC, or a
 *   withdrawn consent). PostHog runs with `cookieless_mode: 'on_reject'` +
 *   `opt_out_capturing_by_default`, which means: NO cookies, NO localStorage /
 *   sessionStorage, NO session replay, NO person profiles, NO identify. Events
 *   carry a sentinel distinct id and PostHog EU derives a daily-rotating,
 *   privacy-preserving hash server-side (project setting "cookieless server
 *   hash mode"). This is aggregate reach measurement without device storage
 *   (ePrivacy art. 5(3) does not apply because nothing is stored on or read
 *   from the device).
 * - `granted` (the visitor opted into the "Analytics" category). Full
 *   persistence (first-party cookie + localStorage), session replay with every
 *   input masked, identify with the Clerk user id, first-touch person props.
 *
 * GPC: the consent hook auto-persists "necessary only" when GPC is present and
 * no decision exists, so GPC lands in `cookieless`. An explicit, later grant
 * by the same visitor wins (same rule as the rest of the consent system).
 */
import type { ConsentState } from '../anonymous-state'
import {
  ANALYTICS_PRODUCTION_HOSTS,
  POSTHOG_CONFIG_DEFAULTS,
  POSTHOG_DEBUG_COOKIE,
  POSTHOG_DEBUG_QUERY,
  POSTHOG_MASK_TEXT_SELECTOR,
  POSTHOG_PROXY_PATH,
  POSTHOG_UI_HOST,
} from './constants'

export type AnalyticsMode = 'granted' | 'cookieless'

/** Decide the analytics mode from the stored consent and the GPC signal. */
export function resolveAnalyticsMode(input: {
  /** Parsed, CURRENT-version consent cookie; `null` when no decision exists. */
  storedConsent: ConsentState | null
  gpc: boolean
}): AnalyticsMode {
  if (!input.storedConsent) return 'cookieless' // no decision (or GPC) = off
  return input.storedConsent.analytics ? 'granted' : 'cookieless'
}

/** Whether the Portfolio Analytics tracker may load (it uses web storage). */
export function portfolioTrackerAllowed(mode: AnalyticsMode): boolean {
  return mode === 'granted'
}

export type ConsentAction =
  | 'opt_in'
  | 'start_replay'
  | 'identify_pending_user'
  | 'set_first_touch'
  | 'stop_replay'
  | 'opt_out'
  | 'clear_consent_record'
  | 'load_portfolio'
  | 'unload_portfolio'

/**
 * Actions to run when the mode changes. `previous === null` is the initial
 * application right after the SDK loaded.
 */
export function consentTransition(
  previous: AnalyticsMode | null,
  next: AnalyticsMode,
): Array<ConsentAction> {
  if (previous === next) return []
  if (next === 'granted') {
    return [
      'opt_in',
      'start_replay',
      'identify_pending_user',
      'set_first_touch',
      'load_portfolio',
    ]
  }
  // next === 'cookieless'
  if (previous === null) return [] // SDK already starts cookieless
  return [
    'stop_replay',
    'opt_out',
    'clear_consent_record',
    'unload_portfolio',
  ]
}

export type AnalyticsEnvironment = { enabled: boolean; debug: boolean }

/**
 * Where analytics may run. Production hosts always; any other host (localhost,
 * preview, e2e) only with the explicit debug switch, so dev and test traffic
 * never pollutes the PostHog project by accident.
 */
export function resolveAnalyticsEnvironment(input: {
  hostname: string
  search: string
  cookie: string
}): AnalyticsEnvironment {
  const isProduction = ANALYTICS_PRODUCTION_HOSTS.includes(input.hostname)
  let debugQuery = false
  try {
    debugQuery = new URLSearchParams(input.search).get(POSTHOG_DEBUG_QUERY) === '1'
  } catch {
    debugQuery = false
  }
  const debugCookie = new RegExp(`(?:^|;\\s*)${POSTHOG_DEBUG_COOKIE}=1(?:;|$)`).test(
    input.cookie,
  )
  const debug = debugQuery || debugCookie
  return { enabled: isProduction || debug, debug }
}

/**
 * Query parameters that may carry secrets or personal data. They are removed
 * from every URL PostHog sees (`$current_url`, `$referrer`, `$pathname`...).
 */
const SENSITIVE_PARAMS = [
  'token',
  'email',
  'e',
  'code',
  'session_id',
  'checkout_session_id',
  'state',
  '__clerk_db_jwt',
  '__clerk_handshake',
  '__clerk_status',
  'unsubscribe',
  'key',
]

/** Strip sensitive query parameters (and the fragment) from a URL string. */
export function sanitizeUrl(raw: string): string {
  if (!raw) return raw
  try {
    const url = new URL(raw)
    for (const name of SENSITIVE_PARAMS) url.searchParams.delete(name)
    url.hash = ''
    return url.toString()
  } catch {
    return raw
  }
}

/**
 * A pathname safe for analytics properties: no query or fragment, opaque
 * tokens / ids (long runs of letters, digits, `-`, `_`, or anything with an
 * `@`) collapsed to `:id`, bounded length.
 */
export function sanitizePathForAnalytics(raw: string): string {
  let path = raw
  try {
    path = new URL(raw, 'https://placeholder.invalid').pathname
  } catch {
    path = raw.split(/[?#]/u)[0] ?? ''
  }
  const segments = path.split('/').map((segment) => {
    let decoded = segment
    try {
      decoded = decodeURIComponent(segment)
    } catch {
      // keep raw
    }
    return decoded.includes('@') || /^[A-Za-z0-9_-]{24,}$/u.test(decoded) || /^\d{6,}$/u.test(decoded)
      ? ':id'
      : segment
  })
  return (segments.join('/') || '/').slice(0, 200)
}

/** Where a visitor came from, relative to our own host. */
export function referrerKind(
  referrer: string,
  ownHost: string,
): 'internal' | 'external' | 'direct' {
  if (!referrer) return 'direct'
  try {
    return new URL(referrer).hostname === ownHost ? 'internal' : 'external'
  } catch {
    return 'direct'
  }
}

/** Static posthog-js init options shared by both modes (pure, tested). */
export function buildPostHogInitOptions(input: { mode: AnalyticsMode }) {
  return {
    api_host: POSTHOG_PROXY_PATH,
    ui_host: POSTHOG_UI_HOST,
    defaults: POSTHOG_CONFIG_DEFAULTS,
    // Consent: pending = rejected = cookieless. `opt_in_capturing()` switches
    // to full persistence once the visitor grants analytics consent.
    cookieless_mode: 'on_reject' as const,
    opt_out_capturing_by_default: true,
    // Our own consent system is authoritative (GPC is handled there).
    respect_dnt: false,
    person_profiles: 'identified_only' as const,
    persistence: 'localStorage+cookie' as const,
    // SPA: `$pageview` / `$pageleave` on every TanStack Router history change.
    capture_pageview: 'history_change' as const,
    capture_pageleave: true,
    // Error tracking + web vitals (LCP, INP, CLS, FCP).
    capture_exceptions: true,
    capture_performance: { web_vitals: true, network_timing: false },
    capture_heatmaps: true,
    capture_dead_clicks: true,
    autocapture: true,
    // Session replay only after consent; inputs and marked text always masked.
    disable_session_recording: input.mode !== 'granted',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: POSTHOG_MASK_TEXT_SELECTOR,
      blockSelector: '[data-ph-block]',
      recordCrossOriginIframes: false,
    },
    mask_all_element_attributes: false,
    mask_all_text: false,
    // Unused products stay off the wire.
    disable_surveys: true,
    disable_conversations: true,
    advanced_disable_feature_flags: true,
    disable_external_dependency_loading: false,
    internal_or_test_user_hostname: null,
    // Never the SDK's `debug` mode: it persists a `ph_debug` key in
    // localStorage, i.e. device storage before consent. Our debug switch only
    // tags events (`environment: 'debug'`).
    debug: false,
  }
}
